import { InstellingBron, RRol } from '@cumlaude/service-contract';

/** Eén waarde van een {@link DashboardOption} met wat die voor de gebruiker doet. */
export type DashboardOptionValue = {
	readonly value: string;
	readonly meaning: string;
	/** Rollen die deze waarde niet kunnen kiezen; het dashboard biedt hem hen niet aan. */
	readonly exceptRollen?: readonly RRol[];
	/** Namen van dashboards waarop deze waarde niet kan, ook al kent het dashboard de optie zelf. */
	readonly exceptDashboards?: readonly string[];
};

/**
 * Een queryparameter die geen filter of groepering is en die de AI-chat kan instellen. Welke dashboards
 * hem ondersteunen volgt uit hun `fixedWeergaveOpties` en `flexibleWeergaveOpties`. Een globale optie geldt voor
 * elk dashboard, tenzij dat hem noemt in zijn `unsupportedOptions`.
 * Het model zet de waarde in `DashboardInfo.options` met `param` als key.
 */
export type DashboardOption = {
	readonly param: string;
	/** AI-gerichte uitleg: wat de optie doet en wanneer het model hem moet zetten. */
	readonly description: string;
	/** De waarden die de optie accepteert, letterlijk zoals de queryparameter ze verwacht. Weglaten bij een vrij formaat dat `description` uitlegt. */
	readonly values?: readonly DashboardOptionValue[];
	readonly default?: string;
	readonly defaultPerRol?: { readonly [rol in RRol]?: string };
	/** De optie bestaat alleen voor scholen met deze bron; de AI-chat krijgt hem bij een andere bron niet. */
	readonly requiredBron?: InstellingBron;
	/** Geldt voor elk dashboard dat de param niet in zijn `unsupportedOptions` noemt. */
	readonly global?: boolean;
};

export const DASHBOARD_OPTIONS = [
	{
		param: 'variant',
		description:
			'Met `Historie` toont het dashboard een trend over meerdere schooljaren; met `Actueel` alleen het gekozen schooljaar. Zet `Historie` zodra de gebruiker een trend of meerdere schooljaren wil zien. Bij `Historie` vervalt het Schooljaar-filter.',
		values: [
			{ value: 'Actueel', meaning: 'Het gekozen schooljaar.' },
			{ value: 'Historie', meaning: 'Een trend over meerdere schooljaren.' },
		],
		default: 'Actueel',
	},
	{
		param: 'eenheid',
		description:
			'Of het dashboard bij het tonen van een trend het aantal leerlingen of het percentage van het totaal laat zien. Zet bij een trend `Aantal`, tenzij de gebruiker om percentages vraagt. Niet op dashboards die deze optie niet kennen.',
		values: [
			{ value: 'Aantal', meaning: 'Het aantal leerlingen.' },
			{ value: 'Percentage', meaning: 'Het percentage van het totaal.' },
		],
		default: 'Aantal',
	},
	{
		param: 'sortOrder',
		description:
			'De sortering van de lijst. Het formaat is `Leerlingen` of `Percentage`; voeg `:desc` toe om aflopend te sorteren (bijv. `Leerlingen:desc`). Handig als de gebruiker de klassen met het hoogste aantal leerlingen of percentage wil zien.',
		global: true,
	},
	{
		param: 'aanwezigheiduitsplitsing',
		description:
			'Waarop het overzicht onderaan Aanwezigheid overzicht de aanwezigheid uitsplitst. Dit is geen groepering: groeperen kan daar niet. Wil de gebruiker de aanwezigheid per leerling, per klas of per vak zien, dan zet je deze optie; een klas of vak selecteer je met een filter. Bestuur kan niet per leerling uitsplitsen.',
		values: [
			{
				value: 'Per leerling',
				meaning: 'Eén regel per leerling, ingedeeld in de drie interventielagen.',
				exceptRollen: [RRol.BESTUUR],
			},
			{ value: 'Per klas', meaning: 'Eén regel per klas, ingedeeld in de drie interventielagen.' },
			{ value: 'Per vak', meaning: 'Eén regel per vak, ingedeeld in de drie interventielagen.' },
		],
		default: 'Per leerling',
		defaultPerRol: { [RRol.BESTUUR]: 'Per klas' },
	},
	{
		param: 'cijfertype',
		description:
			'Welk soort cijfers Toets en Toets details tonen. Zet `Advies` als de gebruiker adviezen van docenten wil zien (vakadviezen); dat kan alleen op Toets, in combinatie met een groepering op leerling (`cf_nm_leerling`), en alleen voor Somtoday-scholen. Zet `Gemiddelde` voor periodegemiddelden, rapportcijfers, SE- en CE-cijfers. Zonder keuze staat het dashboard op `Toets`.',
		values: [
			{ value: '<Alle>', meaning: 'Alle cijfers behalve adviezen, tenzij er op leerling is gegroepeerd.' },
			{ value: 'Toets', meaning: 'Individuele toetsen.' },
			{
				value: 'Advies',
				meaning: 'Adviezen van docenten, zoals vakadviezen. Alleen bij groeperen op leerling.',
				exceptDashboards: ['Toets details'],
			},
			{ value: 'Gemiddelde', meaning: 'Periodegemiddelden, rapportcijfers, SE-, CE- en eindcijfers.' },
		],
		default: 'Toets',
	},
	{
		param: 'dossier',
		description:
			'Of Toets en Toets details de cijfers uit het voortgangsdossier of het examendossier tonen. Heeft geen effect bij cijfertype `Gemiddelde`. Zet `Examendossier` alleen als de gebruiker om het examendossier vraagt.',
		values: [
			{ value: 'Voortgang', meaning: 'Cijfers uit het voortgangsdossier.' },
			{ value: 'Examendossier', meaning: 'Cijfers uit het examendossier.' },
		],
		default: 'Voortgang',
		requiredBron: InstellingBron.Somtoday,
	},
	{
		param: 'doorstroomweergave',
		description:
			'Welke uitkomst van de doorstroom het dashboard toont, als percentage van de leerlingen. Het toont één uitkomst tegelijk: zet de uitkomst waar de gebruiker naar vraagt.',
		values: [
			{ value: 'Succesvol', meaning: 'Leerlingen die succesvol doorstromen.' },
			{ value: 'Niet succesvol', meaning: 'Leerlingen die niet succesvol doorstromen.' },
			{ value: 'Bevorderd', meaning: 'Leerlingen die bevorderd zijn.' },
			{ value: 'Niet bevorderd', meaning: 'Leerlingen die niet bevorderd zijn.' },
			{ value: 'Opstroom', meaning: 'Leerlingen die opstromen naar een hoger niveau.' },
			{ value: 'Afstroom', meaning: 'Leerlingen die afstromen naar een lager niveau.' },
		],
		default: 'Succesvol',
	},
	{
		param: 'kenmerk',
		description: 'Welk leerlingkenmerk het dashboard Kenmerk toont. Zet het kenmerk waar de gebruiker naar vraagt; zonder keuze is dat APCG.',
		values: [
			{
				value: 'ds_is_apcg_van',
				meaning:
					'APCG: leerlingen die wonen in een postcodegebied dat is aangemerkt als armoedeprobleemcumulatiegebied. De inspectie gebruikt dit voor de correctiefactoren op de norm van Onderbouwsnelheid, Bovenbouwsucces en Examencijfers.',
			},
			{
				value: 'ds_fk_ilt_van.ilt_is_lwoo',
				meaning: 'LWOO: leerlingen ingeschreven op een VMBO-opleiding met leerwegondersteunend onderwijs.',
			},
			{
				value: 'ds_fk_ilt_van.ilt_is_vavo',
				meaning:
					'VAVO: leerlingen ingeschreven op een VAVO-opleiding van de eigen instelling, die extern onderwijs volgen op een VAVO-school en daar examen doen.',
			},
			{
				value: 'ds_nm_instroom_in_schooljaar',
				meaning: 'Instroom: of de leerling dit schooljaar intern (van een andere vestiging) of extern instroomt, en of dat tussentijds is.',
			},
			{
				value: 'ds_nm_uitstroom_in_schooljaar',
				meaning:
					'Uitstroom: of de leerling dit schooljaar intern, extern of naar het VAVO uitstroomt, en of dat tussentijds is (vóór 31 juli, of 31 mei voor examenleerlingen).',
			},
			{
				value: 'ds_is_examenopstroom_van',
				meaning:
					'Examenopstroom: leerlingen die eerder voor een lager niveau zijn geslaagd (of een lager diploma hebben) en nu op een hoger niveau doorgaan.',
			},
		],
		default: 'ds_is_apcg_van',
	},
	{
		param: 'cohortrendementtype',
		description: 'Welk soort rendement Cohortrendement en Cohortdetails tonen.',
		values: [
			{ value: 'Cohortrendement tot', meaning: 'Cohortrendement tot een bepaald jaar.' },
			{ value: 'Cohortrendement vanaf', meaning: 'Cohortrendement vanaf een bepaald jaar.' },
			{ value: 'Examenrendement', meaning: 'Rendement van de examenkandidaten.' },
			{ value: 'Opstroomrendement', meaning: 'Rendement van de leerlingen die opstromen.' },
		],
		default: 'Cohortrendement tot',
	},
	{
		param: 'cohortrendementweergave',
		description: 'Wat Cohortrendement per cohort laat zien.',
		values: [
			{ value: 'Cohortrendement', meaning: 'Het percentage leerlingen dat succesvol is.' },
			{ value: 'Doublures', meaning: 'Het aantal doublures.' },
			{ value: 'Niveauverschil', meaning: 'Het verschil in niveau tussen begin en einde.' },
		],
		default: 'Cohortrendement',
	},
	{
		param: 'cijfersseceweergave',
		description: 'Wat SE-CE toont: het verschil tussen het SE- en CE-cijfer, of het SE- of CE-cijfer zelf.',
		values: [
			{ value: 'SE-CE verschil', meaning: 'Het verschil tussen het SE- en het CE-cijfer.' },
			{ value: 'SE-cijfer', meaning: 'De verdeling van het SE-cijfer over 0 tot en met 10.' },
			{ value: 'CE-cijfer', meaning: 'De verdeling van het CE-cijfer over 0 tot en met 10.' },
		],
		default: 'SE-CE verschil',
	},
	{
		param: 'afwijkingpercentiel',
		description: 'Of Examens de afwijking of het percentiel van de cijfers toont.',
		values: [
			{ value: 'Percentiel', meaning: 'Het percentiel van het cijfer.' },
			{ value: 'Afwijking', meaning: 'De afwijking van het cijfer.' },
		],
		default: 'Percentiel',
	},
	{
		param: 'tijdseenheid',
		description: 'In welke eenheid Afwezigheid de afwezigheid toont. De waarden met `(percentage)` tonen het percentage van het totaal.',
		values: [
			{ value: 'Lesuren', meaning: 'Aantal lesuren.' },
			{ value: 'Lestijd', meaning: 'De lestijd.' },
			{ value: 'Lesdagen', meaning: 'Aantal lesdagen.' },
			{ value: 'Lesuren (percentage)', meaning: 'Lesuren als percentage van het totaal.' },
			{ value: 'Lestijd (percentage)', meaning: 'Lestijd als percentage van het totaal.' },
		],
		default: 'Lesuren',
	},
	{
		param: 'interval',
		description: 'Per welke periode de trend op Afwezigheid en Lesregistraties wordt uitgesplitst. Alleen van belang bij de variant `Historie`.',
		values: [
			{ value: 'Schooljaar', meaning: 'Eén punt per schooljaar.' },
			{ value: 'Maand', meaning: 'Eén punt per maand.' },
			{ value: 'Week', meaning: 'Eén punt per week.' },
		],
		default: 'Maand',
	},
	{
		param: 'aspect',
		description: 'Of Leesvaardigheid, Taalverzorging en Rekenen de verdeling over de niveaus of het gemiddelde tonen.',
		values: [
			{ value: 'Verdeling', meaning: 'De verdeling van de leerlingen over de niveaus.' },
			{ value: 'Gemiddelde', meaning: 'Het gemiddelde.' },
		],
		default: 'Verdeling',
	},
	{
		param: 'uitstroomniveauvso',
		description:
			'Of Behaald niveau conform OPP de leerlingen toont die onder, op of boven het niveau van hun ontwikkelingsperspectiefplan uitstromen.',
		values: [
			{ value: 'Onder niveau', meaning: 'Leerlingen die onder het verwachte niveau uitstromen.' },
			{ value: 'Op niveau', meaning: 'Leerlingen die op het verwachte niveau uitstromen.' },
			{ value: 'Boven niveau', meaning: 'Leerlingen die boven het verwachte niveau uitstromen.' },
		],
		default: 'Op niveau',
	},
	{
		param: 'verschilbeoogd',
		description: 'Wat Beoogd i.r.t. behaald einduitstroomniveau toont: het verschil of één van beide niveaus.',
		values: [
			{ value: 'Absoluut verschil', meaning: 'Het absolute verschil tussen beoogd en behaald niveau.' },
			{ value: 'Verschil', meaning: 'Het verschil tussen beoogd en behaald niveau, met richting.' },
			{ value: 'Beoogd niveau', meaning: 'Het beoogde niveau.' },
			{ value: 'Behaald niveau', meaning: 'Het behaalde niveau.' },
		],
		default: 'Absoluut verschil',
	},
	{
		param: 'tussentijdseuitstroomvso',
		description: 'Naar welke vorm van onderwijs Tussentijdse uitstroom de leerlingen toont.',
		values: [
			{ value: 'Regulier onderwijs', meaning: 'Uitstroom naar regulier onderwijs.' },
			{ value: 'Speciaal onderwijs', meaning: 'Uitstroom naar speciaal onderwijs.' },
		],
		default: 'Regulier onderwijs',
	},
	{
		param: 'categorieuitstroomniveauvso',
		description: 'Of Einduitstroom de leerlingen toont die doorstromen naar vervolgonderwijs of naar arbeid.',
		values: [
			{ value: 'Vervolgonderwijs', meaning: 'Leerlingen die naar vervolgonderwijs gaan.' },
			{ value: 'Arbeid', meaning: 'Leerlingen die naar arbeid gaan.' },
		],
		default: 'Vervolgonderwijs',
	},
	{
		param: 'indicator-over',
		description: 'Over hoeveel schooljaren de indicator wordt berekend. Zonder keuze rekent het dashboard over drie schooljaren.',
		values: [
			{ value: '1', meaning: 'Over 1 schooljaar.' },
			{ value: '2', meaning: 'Over 2 schooljaren.' },
			{ value: '3', meaning: 'Over 3 schooljaren.' },
		],
		default: '3',
	},
	{
		param: 'or-uitsluiten',
		description:
			'Welke vestigingen meetellen in de Onderwijsresultaten-dashboards. Zonder keuze tellen alleen vestigingen mee met minimaal 20 leerlingen (30 cijfers bij Examencijfers en Verschil SE-CE).',
		values: [
			{ value: '1', meaning: 'Alleen vestigingen met voldoende leerlingen of cijfers.' },
			{ value: '0', meaning: 'Alle vestigingen.' },
		],
		default: '1',
	},
	{
		param: 'brin-overgang',
		description: 'Of het dashboard de vestigingscode-overgang toont. Zet `1` als de gebruiker de overgang tussen vestigingen wil zien.',
		values: [
			{ value: '1', meaning: 'Toon de vestigingscode-overgang.' },
			{ value: '0', meaning: 'Toon de overgang niet.' },
		],
		default: '0',
	},
	{
		param: 'threshold',
		description: 'Het minimale aantal leerlingen waarmee Plaatsing / Advies een combinatie toont. `0` toont alles.',
		values: [
			{ value: '0', meaning: 'Alle.' },
			{ value: '5', meaning: 'Minimaal 5 leerlingen.' },
			{ value: '10', meaning: 'Minimaal 10 leerlingen.' },
			{ value: '15', meaning: 'Minimaal 15 leerlingen.' },
			{ value: '20', meaning: 'Minimaal 20 leerlingen.' },
			{ value: '30', meaning: 'Minimaal 30 leerlingen.' },
			{ value: '50', meaning: 'Minimaal 50 leerlingen.' },
			{ value: '75', meaning: 'Minimaal 75 leerlingen.' },
			{ value: '100', meaning: 'Minimaal 100 leerlingen.' },
			{ value: '200', meaning: 'Minimaal 200 leerlingen.' },
		],
		default: '0',
	},
	{
		param: 'tekortpunten',
		description:
			'Het minimale aantal tekortpunten waarmee Cijferlijst een leerling toont; `0` toont alle leerlingen. Gebruik deze optie en filter niet zelf op tekortpunten.',
		values: [
			{ value: '0', meaning: 'Alle leerlingen.' },
			{ value: '1', meaning: '1 of meer tekortpunten.' },
			{ value: '2', meaning: '2 of meer tekortpunten.' },
			{ value: '3', meaning: '3 of meer tekortpunten.' },
			{ value: '4', meaning: '4 of meer tekortpunten.' },
			{ value: '5', meaning: '5 of meer tekortpunten.' },
		],
		default: '0',
	},
	{
		param: 'vakOrder',
		description: 'In welke volgorde Cijferlijst de vakken toont: een kolom en een richting, gescheiden door een dubbele punt.',
		values: [
			{ value: 'Tekortpunten:desc', meaning: 'Vakken met de meeste tekortpunten eerst.' },
			{ value: 'Tekortpunten:asc', meaning: 'Vakken met de minste tekortpunten eerst.' },
			{ value: 'Volgnummer:asc', meaning: 'Op volgnummer van het vak, oplopend.' },
			{ value: 'Volgnummer:desc', meaning: 'Op volgnummer van het vak, aflopend.' },
			{ value: 'Alfabetisch:asc', meaning: 'Op naam van het vak, van A tot Z.' },
			{ value: 'Alfabetisch:desc', meaning: 'Op naam van het vak, van Z tot A.' },
		],
		default: 'Tekortpunten:desc',
	},
] as const satisfies ReadonlyArray<DashboardOption>;

export type DashboardOptionDefinition = (typeof DASHBOARD_OPTIONS)[number];

export type DashboardOptionParam = DashboardOptionDefinition['param'];

/** De opties met vaste waarden; alleen voor die staat een enum in `OPTION_ENUMS` in de user-app. */
export type DashboardOptionParamWithValues = Extract<DashboardOptionDefinition, { readonly values: readonly DashboardOptionValue[] }>['param'];

export function findDashboardOption(param: string): DashboardOption | undefined {
	return DASHBOARD_OPTIONS.find((option) => option.param === param);
}

type OptionSupport = {
	readonly fixedWeergaveOpties?: readonly string[];
	readonly flexibleWeergaveOpties?: readonly string[];
	readonly unsupportedOptions?: readonly string[];
};

/** De opties die een dashboard ondersteunt: de genoemde plus de globale die het niet uitsluit. */
export function dashboardOptionsFor(support: OptionSupport): readonly DashboardOptionDefinition[] {
	const weergaveOpties = [...(support.fixedWeergaveOpties ?? []), ...(support.flexibleWeergaveOpties ?? [])];
	return DASHBOARD_OPTIONS.filter((option: DashboardOption) =>
		option.global ? !support.unsupportedOptions?.includes(option.param) : weergaveOpties.includes(option.param)
	);
}

/** Of het dashboard deze waarde van de optie kent; een optie zonder vaste waarden accepteert elke waarde. */
export function isOptionValueAllowed(option: DashboardOption, waarde: string, dashboardName: string): boolean {
	return !option.values || option.values.some((v) => v.value === waarde && !v.exceptDashboards?.includes(dashboardName));
}

export function optionValuesForRol(option: DashboardOption, rol: RRol | undefined): readonly string[] {
	return (option.values ?? []).filter((v) => rol === undefined || !v.exceptRollen?.includes(rol)).map((v) => v.value);
}

export function optionDefaultForRol(option: DashboardOption, rol: RRol | undefined): string | undefined {
	return (rol && option.defaultPerRol?.[rol]) || option.default;
}
